import './Table.scss';
import React, { useState, useCallback, useImperativeHandle, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Table as TableBase } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useLocation } from 'routing';
import { local } from 'services';
import { Help } from 'library';
import { showError } from 'utils';
import { renderFn } from './config';
import { getFiltersProps } from './filters';
import ResizableHeader from './resizableHeader';
import { SettingsButton } from './buttons';
import Context from './context';

const indicator = <LoadingOutlined style={{ fontSize: 24 }} spin />;
const SpinProps = { indicator };

const FetchParamsDefault = { page: 1, filters: {}, sort: {}, take: 25 };
const TableParamsStorage = {};
const SavedParams = {
    get: name => TableParamsStorage[name] || FetchParamsDefault,
    set: (name, params) => TableParamsStorage[name] = { ...params }
};
let fetchRowId;

const UserPrefs = {
    get: name => local.custom(name, true)() || {},
    set: (name, value) => local.custom(name, true)(value)
};

const DefaultPaginationProps = {
    pageSizeOptions: ['25', '50', '100', '200'],
    showTotal: false,
    showSizeChanger: true,
    hideOnSinglePage: false,
    size: 'small',
    position: ['bottomLeft'],
    showLessItems: false,
};

const Table = React.forwardRef(({
    name,
    fetchAction,
    rowKey = 'id',
    className,
    rowClassName = () => { },
    persistentFilters = {},
    onTotalChange = () => { },
    onRowAction,
    defaultPageSize = 25,
    header = null,
    columns: defaultColumns
}, ref) => {
    const filterRef = React.useRef();

    const location = useLocation();
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({});
    const [columns, setColumns] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchParams, setFetchParams] = useState(FetchParamsDefault);

    const isInitialMount = React.useRef(true);

    const fetch = useCallback((parameters, skipLoader = false, forColumns = columns) => {
        let params = { ...parameters };
        if (Object.isEmpty(params)) {
            params = SavedParams.get(name);
        }
        SavedParams.set(name, params);

        if (!skipLoader) {
            setLoading(SpinProps);
        }

        const { page, take, filters, sort } = params;
        const $filters = {
            ...persistentFilters,
            ...filters
        };

        const visibleColumns = forColumns.filter(item => !item.hide);

        return fetchAction({ page, take, filters: $filters, sort }, visibleColumns)
            .then((res) => {
                if (res) {
                    const { total } = res;
                    setPagination(oldPagination => ({
                        ...oldPagination,
                        current: page,
                        total,
                        pageSize: take
                    }));
                    setData(res.data);
                    setFetchParams({
                        page, take, filters, sort, total
                    });
                    if (onTotalChange) {
                        onTotalChange(total);
                    }
                }
            })
            .catch(showError)
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchAction, name, onTotalChange, persistentFilters]);

    const updateUserPrefs = useCallback((cols) => {
        const dataToSave = {
            pageSize: pagination.pageSize,
            cols: {}
        };

        cols.forEach(({ dataIndex, width, hide }) => {
            if (dataIndex) {
                dataToSave.cols[dataIndex] = {
                    width,
                    hide
                };
            }
        });
        UserPrefs.set(name, dataToSave);
    }, [name, pagination.pageSize]);

    useEffect(() => {
        const userPrefs = UserPrefs.get(name) || { cols: {} };
        const savedFetchParams = SavedParams.get(name);

        const nextColumns = defaultColumns
            .map((column, index) => {
                const { key, className: columnClassName } = column;
                let { title } = column;

                const filterProps = getFiltersProps(column, filterRef);
                const display = column.display || title;

                const hasPrefs = userPrefs.cols && userPrefs.cols[key];
                const width = hasPrefs ? (userPrefs.cols[key].width || column.width) : column.width;
                const hide = column.hidden || (hasPrefs ? userPrefs.cols[key].hide : !!column.hide);

                if (column.settings) {
                    title = <SettingsButton />;
                }
                if (column.tooltip) {
                    title = (
                        <>
                            <Help> {column.tooltip} </Help>
                            {title}
                        </>
                    );
                }
                // Filter
                const savedFilter = savedFetchParams.filters[key];
                const filteredValue = savedFilter || column.defaultFilteredValue || null;

                // Sort
                const savedSortOrder = savedFetchParams.sort.field === key
                    ? savedFetchParams.sort.order : undefined;
                const sortOrder = savedSortOrder || column.defaultSortOrder;
                return {
                    key,
                    ...column,
                    ...filterProps,
                    width,
                    hide,
                    display, // for settings
                    title,
                    dataIndex: key,
                    sorter: !!column.sort,
                    sortBy: column.sortBy || key,
                    render: column.render || renderFn[column.type] || null,
                    sortOrder,
                    ellipsis: false,
                    filteredValue,
                    className: columnClassName || '',
                    onHeaderCell: ({ width: newWidth }) => ({
                        width: newWidth,
                        index,
                    }),
                };
            });
        setColumns(nextColumns);

        // Page
        const pageSize = savedFetchParams.take || userPrefs.pageSize || defaultPageSize;
        const defaultPage = savedFetchParams.page || 1;

        // Filters
        const defaultFilters = { ...savedFetchParams.filters } || {};
        defaultColumns.filter(col => col.defaultFilteredValue)
            .forEach((col) => {
                defaultFilters[col.key] = col.defaultFilteredValue;
            });

        // Sort
        const defaultSortColumn = nextColumns.find(col => col.defaultSortOrder);
        const defaultSort = {
            field: defaultSortColumn?.sortBy || 'id',
            order: defaultSortColumn?.defaultSortOrder || 'ascend'
        };

        setPagination({
            ...DefaultPaginationProps,
            defaultPageSize: savedFetchParams.take || pageSize,
            defaultCurrent: defaultPage,
        });

        const defaultParams = {
            page: defaultPage,
            take: defaultPageSize,
            sort: defaultSort,
            filters: defaultFilters,
        };
        fetch(defaultParams, false, nextColumns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultColumns.length, defaultPageSize]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(persistentFilters)]);

    const removeRow = useCallback((row) => {
        const nextData = data.filter(item => item.id !== row.id);
        setData(nextData);
    }, [data]);

    const fetchRow = useCallback(async (prop, propName = rowKey) => {
        const changeRowLoading = (idx, $loading) => {
            const row = data[idx];
            const nextData = Array.changeItem(data, { ...row, $loading });
            setData(nextData);
        };

        const idx = data.findIndex(i => i[propName] === prop);
        if (idx < 0) {
            fetch();
            return;
        }

        changeRowLoading(idx, true);

        const { data: updatedRow } = await fetchAction({ 
            page: 1, take: 1, filters: { [propName]: prop }
        })
            .catch(showError)
            .finally(() => changeRowLoading(idx, false));

        if (updatedRow && updatedRow.length) {
            setData((oldData) => {
                const nextData = Array.changeItem(oldData, updatedRow[0]);
                return nextData;
            });
        }
    }, [data, fetch, fetchAction, rowKey]);

    const clearFilters = useCallback(() => {
        const nextColumns = columns.map(column => ({
            ...column,
            filteredValue: null,
        }));
        setColumns(nextColumns);
        fetch({ ...fetchParams, filters: {} });
    }, [columns, fetch, fetchParams]);

    const handleTableChange = (newPagination, filters, sorter) => {
        setPagination(newPagination);
        let sortByField = '';

        const nextColumns = columns.map((column) => {
            const isSort = sorter.field === column.key || !!column.defaultSortOrder;
            if (isSort) {
                sortByField = column.sortBy || column.key;
            }
            return {
                ...column,
                filteredValue: filters[column.key],
                sortOrder: sorter.field === column.key && sorter.order,
            };
        });

        setColumns(nextColumns);
        const params = {
            take: newPagination.pageSize,
            page: newPagination.current,
            sort: {
                field: sortByField || sorter.field,
                order: sorter.order
            },
            filters,
        };
        fetch(params);
    };

    const changeColumns = useCallback((nextColumns, reload = false) => {
        setColumns(nextColumns);
        updateUserPrefs(nextColumns);
        if (reload) {
            fetch(null, false, nextColumns);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSelectionAllChange = (areSelected, _, changed) => {
        let newSelected = [];
        if (areSelected) {
            newSelected = [...selected, ...changed].unique();
        } else {
            const ids = changed.map(item => item[rowKey]);
            newSelected = selected.filter(item => !ids.includes(item.id));
        }
        setSelected(newSelected);
    };

    const onSelectionChange = (record, isSelected) => {
        onSelectionAllChange(isSelected, [], [record]);
    };

    const onRow = record => ({
        onClick: () => {
            if (editMode) {
                const select = !selected.find(item => item[rowKey] === record[rowKey]);
                onSelectionChange(record, select);
            } else if (onRowAction) {
                onRowAction(record);
            }
        }
    });

    const updateEditMode = useCallback((value) => {
        const nextColumns = [...columns];
        nextColumns[0].hide = value;
        setColumns(nextColumns);
        setEditMode(value);
        if (!value) {
            setSelected([]);
        }
    }, [columns]);

    const stateFetchRowId = location.state?.tableFetchRowId;

    useEffect(() => {
        if (fetchRowId !== stateFetchRowId) {
            fetchRow(stateFetchRowId);
            fetchRowId = stateFetchRowId;
        }
    }, [fetchRow, stateFetchRowId]);

    const filteredColumns = columns.filter(i => !i.hide);
    const selectedRowKeys = selected.map(item => item[rowKey]);

    const contextValue = React.useMemo(
        () => ({
            columns,
            changeColumns,
            fetch,
            fetchRow,
            fetchParams,
            fetchAction,
            clearFilters,
            removeRow,
            editMode,
            updateEditMode,
            selected,
            loading: !!loading,
            updateUserPrefs,
            data,
            pageSize: pagination.pageSize,
            persistentFilters
        }),
        [
            clearFilters, columns, changeColumns, editMode,
            fetch, fetchAction, fetchParams,
            fetchRow, loading, removeRow,
            selected, updateEditMode, updateUserPrefs,
            data, pagination.pageSize,
            persistentFilters
        ]
    );

    useImperativeHandle(ref, () => ({
        fetch,
        fetchRow,
        columns
    }));

    const onRowClassName = item => (item.$loading ? 'loading' : rowClassName(item));

    return (
        <Context.Provider
            value={contextValue}
        >
            {header}
            <TableBase
                key={name}
                components={{
                    header: {
                        cell: ResizableHeader,
                    }
                }}
                className={`table ${onRowAction ? 'clickable' : ''} ${className}`}
                loading={loading}
                rowKey={rowKey}
                dataSource={data}
                columns={filteredColumns}
                pagination={pagination}
                onChange={handleTableChange}
                onRow={onRow}
                showSorterTooltip={{ target: 'sorter-icon' }}
                size="small"
                scroll={{ x: 'max-content', y: 0 }}
                rowClassName={onRowClassName}
                rowSelection={editMode ? {
                    type: 'checkbox',
                    selectedRowKeys,
                    onSelect: onSelectionChange,
                    onSelectAll: onSelectionAllChange,
                    preserveSelectedRowKeys: true
                } : undefined}
            />
        </Context.Provider>
    );
});

Table.propTypes = {
    name: PropTypes.string.isRequired,
    rowKey: PropTypes.string,
    fetchAction: PropTypes.func.isRequired,
    columns: PropTypes.array.isRequired,
    /* @TODONEXT column props
    title: 'Location',
    key: 'locationId',
    hide: true/false, - defaultHide, switchable via table settings
    hidden: true/false  - always hidden
    type: ColumnTypes,
    width: 160,
    ellipsis: true,
    render: (value, row) => row.title,
    filterService: LocationService.options,
    filter: true,
    sort: 'title',
    className: ''
    */
    onRowAction: PropTypes.func,
    onTotalChange: PropTypes.func,
    rowClassName: PropTypes.func,
    persistentFilters: PropTypes.object,
    defaultPageSize: PropTypes.number,
    header: PropTypes.element,
    className: PropTypes.string
};

export { Table, TableBase };
